
if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      // Delay until children are parsed
      window.requestAnimationFrame(() => this._initialize());
    }

    _initialize() {
      // 1) Grab core elements
      const liveRegion = this.querySelector('[id^="GalleryStatus"]');
      const viewer     = this.querySelector('[id^="GalleryViewer"]');
      const thumbnails = this.querySelector('[id^="GalleryThumbnails"]');

      console.log('📦 [MediaGallery] init:', { viewer, thumbnails });

      if (!viewer) {
        console.warn('⚠️ [MediaGallery] No viewer found — aborting.');
        return;
      }

      this.elements = { liveRegion, viewer, thumbnails };
      this.mql = window.matchMedia('(min-width: 750px)');

      // 2) Sync slider navigation
      viewer.addEventListener(
        'slideChanged',
        debounce(this.onSlideChanged.bind(this), 500)
      );

      // 3) Always wire up clicks on the thumbnail images
      const triggers = this.querySelectorAll('img.product__thumbnail-trigger');
      console.log(`🔌 [MediaGallery] Wiring ${triggers.length} thumbnail-trigger images`);
      triggers.forEach(img => {
        console.log('   • trigger:', img);
        img.addEventListener('click',  () => this._activate(img.dataset.target));
        img.addEventListener('keydown', e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this._activate(img.dataset.target);
          }
        });
      });

      // 4) If we have a thumbnail slider and desktop layout, strip semantics
      if (thumbnails && this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) {
        this.removeListSemantic();
      }
    }

    _activate(mediaId) {
      console.log(`🖱️ [MediaGallery] Thumbnail click → mediaId="${mediaId}"`);
      this.setActiveMedia(mediaId, true);
    }

    onSlideChanged(event) {
      const mediaId = event.detail.currentElement.dataset.mediaId;
      console.log('🔄 [MediaGallery] slideChanged → mediaId=', mediaId);
      const thumb = this.elements.thumbnails?.querySelector(
        `img.product__thumbnail-trigger[data-target="${mediaId}"]`
      );
      this.setActiveThumbnail(thumb);
    }

    setActiveMedia(mediaId, prepend) {
      console.log(`▶️ [MediaGallery] setActiveMedia(mediaId=${mediaId}, prepend=${prepend})`);
      const { viewer, thumbnails } = this.elements;
      const slides = viewer.querySelectorAll('[data-media-id]');
      console.log(`   ↪ Found ${slides.length} slides`);
      slides.forEach(slide => {
        console.log('      •', slide, slide.dataset.mediaId, slide.className);
      });

      const activeSlide = viewer.querySelector(`[data-media-id="${mediaId}"]`) || slides[0];
      if (!activeSlide) {
        console.error('❌ [MediaGallery] No slide matched mediaId=', mediaId);
        return;
      }
      console.log('   ↪ Active slide:', activeSlide);

      // 1) Toggle is-active
      slides.forEach(slide => slide.classList.remove('is-active'));
      activeSlide.classList.add('is-active');

      // 2) Prepend in viewer if requested
      if (prepend) {
        const wrapper = activeSlide.parentElement;
        if (wrapper.firstChild !== activeSlide) wrapper.prepend(activeSlide);
        if (viewer.slider) {
          console.log('   ↺ [MediaGallery] Resetting pages');
          viewer.resetPages();
        }
      }

      this.preventStickyHeader();
      setTimeout(() => {
        activeSlide.parentElement.scrollTo({ left: activeSlide.offsetLeft });
        const rect = activeSlide.getBoundingClientRect();
        if (rect.top <= -0.5) {
          window.scrollTo({ top: rect.top + window.scrollY, behavior: 'smooth' });
        }
      });

      this.playActiveMedia(activeSlide);

      // 3) Update thumbnail focus & position if thumbnails exist
      if (thumbnails) {
        const thumb = thumbnails.querySelector(
          `img.product__thumbnail-trigger[data-target="${mediaId}"]`
        );
        if (prepend && thumb && thumb.parentElement.firstChild !== thumb) {
          thumb.parentElement.prepend(thumb);
        }
        this.setActiveThumbnail(thumb);
        this.announceLiveRegion(activeSlide, thumb?.dataset.mediaPosition);
      }
    }

    setActiveThumbnail(thumb) {
      if (!thumb) {
        console.warn('⚠️ [MediaGallery] setActiveThumbnail: no thumbnail provided');
        return;
      }
      console.log('🏷️ [MediaGallery] setActiveThumbnail →', thumb);
      const { thumbnails } = this.elements;
      thumbnails
        .querySelectorAll('img.product__thumbnail-trigger')
        .forEach(img => img.removeAttribute('aria-current'));
      thumb.setAttribute('aria-current', 'true');
      if (!thumbnails.isSlideVisible(thumb, 10)) {
        thumbnails.slider.scrollTo({ left: thumb.offsetLeft });
      }
    }

    announceLiveRegion(activeSlide, position) {
      const img = activeSlide.querySelector('img');
      if (!img || !this.elements.liveRegion) return;
      img.onload = () => {
        const lr = this.elements.liveRegion;
        lr.removeAttribute('aria-hidden');
        lr.textContent = window.accessibilityStrings.imageAvailable.replace('[index]', position);
        setTimeout(() => lr.setAttribute('aria-hidden', 'true'), 2000);
      };
      img.src = img.src;
    }

    playActiveMedia(activeSlide) {
      console.log('▶️ [MediaGallery] playActiveMedia', activeSlide);
      window.pauseAllMedia();
      const deferred = activeSlide.querySelector('.deferred-media');
      if (deferred) deferred.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (this.stickyHeader) {
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }
    }

    removeListSemantic() {
      console.log('🏗️ [MediaGallery] removeListSemantic');
      const slider = this.elements.viewer.slider;
      if (!slider) return;
      slider.setAttribute('role', 'presentation');
      slider.sliderItems.forEach(item => item.setAttribute('role', 'presentation'));
    }
  });
}
